import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import SlideToggle from '../../../modules/SlideToggle';
import { ChartData, GanttChartProcess, ProcessData } from './GanttChartData';

interface Props {
  /** 整形されたガントチャート用の日程データ */
  ganttChartList: ChartData[];
  /** 資料管理No.の子日程を表示するかどうか */
  isShowChild: (targetProductionNo: string) => boolean;
  /** 資料管理No.の子日程表示非表示切り替え */
  changeChildList: (targetProductionNo: string) => void;
}

/**
 * ガントチャート表示部分
 * @param param0
 * @returns
 */
function GanttChart({ ganttChartList, isShowChild, changeChildList }: Props) {
  // ガントチャート情報をtr要素に変換
  return (
    <div className="tbody23">
      {ganttChartList.map((chart, i) => (
        <ConvertRow
          key={`psVersionId:${chart.ProductionId}+${i.toString()}`}
          chart={chart}
          isShowChild={isShowChild}
          changeChildList={changeChildList}
        />
      ))}
    </div>
  );
}

interface RowProps {
  chart: ChartData;
  isShowChild: (targetPsVersionId: string) => boolean;
  changeChildList: (targetPsVersionId: string) => void;
}

/**
 * ガントチャートtr要素変換
 * @param param0
 * @returns
 */
function ConvertRow({ chart, isShowChild, changeChildList }: RowProps) {
  const isShowChildFlg = isShowChild(chart.parent_production_no ?? '');
  const {
    i18n: { language },
  } = useTranslation();

  return (
    <SlideToggle open={(isShowChildFlg && chart.isChild) || !chart.isChild}>
      <div
        className={`tr23 ${chart.isChild ? 'childtr' : ''}`}
        onClick={() => {
          // 親日程をクリックしたときに、子日程の表示非表示を切り替える
          changeChildList(chart.production_no);
        }}
      >
        {!chart.isChild && (
          <>
            <div className="sticky td23">
              {/* 親日程のみ資料管理No.クリックで別画面に表示 */}
              <Link
                to={`/${language}/productions/edit/${chart.ProductionId?.toString() ?? ''}#schedule`}
                target="_blank"
              >
                {chart.production_no}
              </Link>
            </div>
            {/* 機種名 */}
            <div className="sticky td23">{chart.modelName}</div>
            {/* M/Y */}
            <div className="sticky td23">{chart.salesMy}</div>
          </>
        )}
        {chart.isChild && (
          <>
            {/* 機種名 */}
            <div className="sticky td23">
              <div>└{chart.production_no}</div>
            </div>
            {/* 資料管理No. */}
            <div className="sticky td23">
              <div>{chart.modelName}</div>
            </div>
            {/* M/Y */}
            <div className="sticky td23">
              <div>{chart.salesMy}</div>
            </div>
          </>
        )}
        {/* チャート部分 */}
        {chart.processList.map((process, num) => (
          <div
            key={`${chart.production_no} + ${num.toString()}`}
            className={`td23 ${chart.isChild && !isShowChildFlg ? 'none-padding' : ''}`}
          >
            <Td chart={process} />
          </div>
        ))}
      </div>
    </SlideToggle>
  );
}
interface monthProps {
  chart: GanttChartProcess;
}

/**
 * ガントチャート スケジュールデータ用コンポーネント
 * @param param0
 * @returns
 */
function Td({ chart: Chart }: monthProps) {
  // データが存在しない場合、空白セルを返却
  if (Chart === undefined || Chart.step1 === undefined || Chart.step2 === undefined) {
    return <div key="td23 blank" />;
  }
  const blankStep = Chart.step1.length - Chart.step2.length;

  let blankStepClassName = '';
  // step1がstep2より多い場合
  if (blankStep > 0) {
    // 不足しているのはstep2
    blankStepClassName = 'step2';
  } else {
    blankStepClassName = 'step1';
  }
  const step1Flg = Chart.step1.length > 0 || blankStep < 0;
  const step2Flg = Chart.step2.length > 0 || blankStep > 0;

  return (
    <>
      {step1Flg && (
        <div className="step-block">
          {/* ガントチャート 予定表示 */}
          {Chart.step1.map((process, num) => (
            <div
              key={`${process.fromDate ?? ''} + step1 + ${num.toString()}`}
              className="step1 step"
              style={{ paddingLeft: process.paddingLeft }}
            >
              <ChartCell process={process} />
            </div>
          ))}
          {/* 予定表示セルが少ない場合に差分を作成 */}
          {blankStep < 0 && BlankStep({ className: blankStepClassName, cnt: -blankStep })}
        </div>
      )}
      {step2Flg && (
        <div className="step-block">
          {/* ガントチャート 実績表示 */}
          {Chart.step2.map((process, num) => (
            <div
              key={`${process.fromDate ?? ''} + step2 + ${num.toString()}`}
              className="step2 step"
              style={{ paddingLeft: process.paddingLeft }}
            >
              <ChartCell process={process} />
            </div>
          ))}
          {/* 実績表示セルが少ない場合に差分を作成 */}
          {blankStep > 0 && BlankStep({ className: blankStepClassName, cnt: blankStep })}
        </div>
      )}
    </>
  );
}

interface ChartCellProp {
  process: ProcessData;
}
/**
 * チャートセル用コンポーネント
 * @returns
 */
function ChartCell({ process }: ChartCellProp) {
  return (
    <span
      className={`col2fr ${process.color}`}
      data-from-date={process.fromDate}
      data-period={process.period}
      style={{ width: process.width }}
    >
      {process.name}
    </span>
  );
}
interface pro {
  className: string;
  cnt: number;
}
/**
 * 空白セルコンポーネント
 * @returns
 */
function BlankStep({ className, cnt }: pro) {
  const blankSteps = [];
  for (let x = 0; x < cnt; x += 1) {
    blankSteps.push(<div key={`${className} ${x.toString()}`} className={`${className} step`} />);
  }
  return blankSteps;
}
export default GanttChart;
